import { useEffect, useMemo, useState } from 'react';
import '../styles/dashboard-progress-management.css';
import EditableRecordTable from '../components/EditableRecordTable.jsx';
import DashboardInputLayout from '../components/DashboardInputLayout.jsx';
import AdvancedFilterPanel from '../components/AdvancedFilterPanel.jsx';
import useAdvancedFilter from '../hooks/useAdvancedFilter.js';
import DashboardInputToolbar from '../components/DashboardInputToolbar.jsx';
import {
  createProblem,
  deleteProblem,
  getActors,
  getCauses,
  getEmployees,
  getImpacts,
  getLargeCategories,
  getMiddleCategories,
  getSmallCategories,
  getProblems,
  getScheduleOemRegionPairs,
  getRisks,
  getStatuses,
  updateProblem,
} from '../api/dashboardApi.js';
import { exportToExcel } from '../utils/exportUtils.js';
import ExportConfirmPopup from '../components/ExportConfirmPopup.jsx';
import { getUserName } from '../../../auth/auth.js';
import { canEdit } from '../../../auth/permissions.js';
import {
  buildLookupMap,
  buildOptionsByOemRegion,
  buildSmallCategoryOptionsByMiddle,
  buildSmallCategoryOptionsByMiddleId,
  buildSmallCategoryLookupByMiddleId,
} from '../utils/converters.js';
import {
  buildMiddleCategoryOptionsByLarge,
} from '../utils/riskConverters.js';
import { buildSelectOptions, toPayload } from '../utils/problemConverters.js';

const BASE_COLUMNS = [
  { key: 'deadlineCheck', label: '期限チェック', type: 'text', width: '120px', readOnly: true },
  { key: 'noId', label: 'ID', type: 'text', width: '80px', readOnly: true },
  { key: 'summary', label: 'タイトル', type: 'text', width: '260px', required: true },
  { key: 'oemRegion', label: 'OEM×対地', type: 'select', width: '180px', required: true },
  { key: 'ownerRegion', label: '領域', type: 'select', width: '140px', required: true },
  { key: 'largeCategory', label: '大項目', type: 'select', width: '160px' },
  { key: 'middleCategory', label: '中項目', type: 'select', width: '160px' },
  { key: 'smallItem', label: '小項目', type: 'select', width: '180px' },
  { key: 'todo', label: 'ToDo', type: 'checkbox', width: '100px' },
  { key: 'projectIssue', label: 'PJ全体課題', type: 'checkbox', width: '130px' },
  { key: 'owner', label: '担当者', type: 'tags', width: '150px', required: true },
  { key: 'status', label: 'ステータス', type: 'select', width: '130px', required: true },
  { key: 'issueOrigin', label: '課題起因', type: 'select', width: '160px', required: true },
  { key: 'impact', label: '影響度', type: 'select', width: '120px', required: true },
  { key: 'closeCondition', label: 'クローズ条件', type: 'text', width: '260px', required: true },
  { key: 'dueDate', label: '対応期限', type: 'date', width: '140px', required: true },
  { key: 'policyDecisionDate', label: '対応方針決定日', type: 'date', width: '160px' },
  { key: 'policyDetail', label: '対応方針詳細', type: 'text', width: '260px' },
  { key: 'ownerAreaCompletedAt', label: '担当領域完了日', type: 'date', width: '160px' },
  { key: 'createdAt', label: '作成日時', type: 'text', readOnly: true },
  { key: 'createdBy', label: '作成者', type: 'text', readOnly: true },
  { key: 'updatedAt', label: '最終更新日時', type: 'text', readOnly: true },
  { key: 'updatedBy', label: '最終更新者', type: 'text', readOnly: true },
  { key: 'pmoConfirmCompletedAt', label: 'PMO確認完了日', type: 'date', width: '160px' },
  { key: 'scheduleIssue', label: 'スケジュール課題', type: 'checkbox', width: '170px' },
  { key: 'riskEmergenceIssue', label: 'リスク顕在化課題', type: 'checkbox', width: '170px' },
  { key: 'issueByRiskEmergence', label: 'リスク顕在化による課題移行日', type: 'date', width: '240px' },
  { key: 'riskManagement', label: '関連リスク', type: 'tags', width: '180px' },
];

function createEmptyRow(/* istanbul ignore next -- 呼び出し元は常に defaults を渡す */ defaults = {}) {
  return BASE_COLUMNS.reduce((acc, column) => {
    if (!(column.key in acc)) {
      acc[column.key] = column.type === 'checkbox' ? false : '';
    }
    return acc;
  }, { ...defaults, __isNew: true });
}

export default function DashboardProblemManagementPage() {
  const [rows, setRows] = useState([]);
  const [editingRows, setEditingRows] = useState([]);
  const [statusByName, setStatusByName] = useState(new Map());
  const [actorByName, setActorByName] = useState(new Map());
  const [causeByName, setCauseByName] = useState(new Map());
  const [impactByName, setImpactByName] = useState(new Map());
  const [statusOptions, setStatusOptions] = useState(['']);
  const [actorOptions, setActorOptions] = useState(['']);
  const [causeOptions, setCauseOptions] = useState(['']);
  const [impactOptions, setImpactOptions] = useState(['']);
  const [riskManagementOptionsByOemRegion, setRiskManagementOptionsByOemRegion] = useState({});
  const [riskByName, setRiskByName] = useState(new Map());
  const [largeCategoryOptions, setLargeCategoryOptions] = useState(['']);
  const [middleCategoryOptionsByLarge, setMiddleCategoryOptionsByLarge] = useState({});
  const [smallCategoryOptionsByMiddle, setSmallCategoryOptionsByMiddle] = useState({});
  const [smallCategoryOptionsByMiddleId, setSmallCategoryOptionsByMiddleId] = useState({});
  const [largeByName, setLargeByName] = useState(new Map());
  const [middleByLargePair, setMiddleByLargePair] = useState(new Map());
  const [smallByMiddleIdPair, setSmallByMiddleIdPair] = useState(new Map());
  const [employeeByName, setEmployeeByName] = useState(new Map());
  const [employeeOptions, setEmployeeOptions] = useState(['']);
  const [oemRegionOptions, setOemRegionOptions] = useState(['']);
  const [oemRegionLookup, setOemRegionLookup] = useState(new Map());
  const [defaultCreateValues, setDefaultCreateValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportPopupOpen, setExportPopupOpen] = useState(false);
  const [addSignal, setAddSignal] = useState(0);
  const [showFilters, setShowFilters] = useState(true);
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  const {
    filterValues: problemFilterValues,
    filterMode,
    setFilterMode,
    handleFieldChange: handleProblemFilterChange,
    clearFilters,
    buildFilterPredicate,
  } = useAdvancedFilter({
    initialValues: {
      deadlineCheck: [],
      oemRegion: [],
      status: [],
      ownerRegion: [],
      largeCategory: [],
      middleCategory: [],
      smallItem: [],
      todo: [],
      projectIssue: [],
      owner: getUserName() ? [getUserName()] : [],
      date_from: '',
      date_to: '',
      keyword: '',
    },
    initialMode: 'AND',
  });

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError('');
        const [
          data,
          statusData,
          actorData,
          causeData,
          impactData,
          oemRegionPairs,
          riskData,
          largeCategoryData,
          middleCategoryData,
          smallCategoryData,
          employeeData,
        ] = await Promise.all([
          getProblems(),
          getStatuses(),
          getActors(),
          getCauses(),
          getImpacts(),
          getScheduleOemRegionPairs(),
          getRisks(),
          getLargeCategories(),
          getMiddleCategories(),
          getSmallCategories(),
          getEmployees(),
        ]);
        if (!active) return;
        setRows(data);
        setStatusByName(buildLookupMap(statusData, 'status'));
        setActorByName(buildLookupMap(actorData, 'actor'));
        setCauseByName(buildLookupMap(causeData, 'cause'));
        setImpactByName(buildLookupMap(impactData, 'impact'));
        setStatusOptions(['', ...statusData.map((item) => item.status).filter(Boolean)]);
        setActorOptions(['', ...actorData.map((item) => item.actor).filter(Boolean)]);
        setCauseOptions(['', ...causeData.map((item) => item.cause).filter(Boolean)]);
        setImpactOptions(['', ...impactData.map((item) => item.impact).filter(Boolean)]);
        // 関連リスクの候補は行のOEM×対地に限定する（別OEM×対地への誤リンク防止）
        setRiskManagementOptionsByOemRegion(buildOptionsByOemRegion(riskData, [
          (item) => item.title || item.riskManagement,
        ]));
        const riskMap = new Map();
        riskData.forEach((item) => {
          const name = item.title || item.riskManagement;
          if (name) riskMap.set(name, item.id);
        });
        setRiskByName(riskMap);
        setLargeCategoryOptions([
          '',
          ...Array.from(new Set(largeCategoryData.map((item) => item.large_category).filter(Boolean))),
        ]);
        setMiddleCategoryOptionsByLarge(buildMiddleCategoryOptionsByLarge(middleCategoryData));
        // フィルタ用は中項目名キー（同名跨りは名前一致でよい）、グリッド入力用は中項目IDキー（厳密）
        setSmallCategoryOptionsByMiddle(buildSmallCategoryOptionsByMiddle(smallCategoryData));
        setSmallCategoryOptionsByMiddleId(buildSmallCategoryOptionsByMiddleId(smallCategoryData));

        // カテゴリ名→ID のルックアップ（課題自身のカテゴリFKを解決するため）
        const largeNameMap = new Map();
        largeCategoryData.forEach((item) => {
          if (item.large_category && item.id) largeNameMap.set(item.large_category, item.id);
        });
        setLargeByName(largeNameMap);
        const middlePairMap = new Map();
        middleCategoryData.forEach((item) => {
          const largeName = item.large_category?.name;
          if (largeName && item.middle_category && item.id) {
            middlePairMap.set(`${largeName}::${item.middle_category}`, item.id);
          }
        });
        setMiddleByLargePair(middlePairMap);
        setSmallByMiddleIdPair(buildSmallCategoryLookupByMiddleId(smallCategoryData));
        setEmployeeByName(buildLookupMap(employeeData, 'user_name', 'employee_id'));
        setEmployeeOptions(['', ...employeeData.map((item) => item.user_name).filter(Boolean)]);
        const oemRegionOpts = [''];
        const oemRegionMap = new Map();
        oemRegionPairs.forEach((p) => {
          if (!oemRegionMap.has(p.label)) {
            oemRegionOpts.push(p.label);
            oemRegionMap.set(p.label, { oemId: p.oem_id, regionId: p.region_id });
          }
        });
        setOemRegionOptions(oemRegionOpts);
        setOemRegionLookup(oemRegionMap);
        setDefaultCreateValues({
          oemId: oemRegionPairs[0]?.oem_id,
          regionId: oemRegionPairs[0]?.region_id,
          status: '未着手',
        });
      } catch (err) {
        if (!active) return;
        setError('課題管理データの取得に失敗しました');
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, []);

  const selectColumnKeys = useMemo(
    () => BASE_COLUMNS.filter((column) => column.type === 'select').map((column) => column.key),
    [],
  );

  const selectOptions = useMemo(
    () => buildSelectOptions(editingRows.length ? editingRows : rows, selectColumnKeys),
    [editingRows, rows, selectColumnKeys],
  );

  const columns = useMemo(
    () =>
      BASE_COLUMNS.map((col) => {
        if (col.key === 'largeCategory') {
          return {
            ...col,
            width: /* istanbul ignore next -- この列は BASE_COLUMNS で width を指定済み */ (col.width || '148px'),
            options: largeCategoryOptions,
            onChange: ({ nextRow, value }) => {
              const middleOptions = middleCategoryOptionsByLarge[value] || [''];
              const keepMiddle = middleOptions.includes(nextRow.middleCategory || '');

              // 小項目候補は大項目込みで解決した中項目IDでスコープ（同名中項目の衝突回避）
              const middleId = keepMiddle
                ? middleByLargePair.get(`${value || ''}::${nextRow.middleCategory || ''}`)
                : null;
              const smallOptions = (middleId != null && smallCategoryOptionsByMiddleId[middleId]) || [''];
              const keepSmall = keepMiddle && smallOptions.includes(nextRow.smallItem || '');

              return {
                ...nextRow,
                middleCategory: keepMiddle ? (nextRow.middleCategory || '') : '',
                smallItem: keepSmall ? (nextRow.smallItem || '') : '',
              };
            },
          };
        }

        if (col.key === 'middleCategory') {
          return {
            ...col,
            width: /* istanbul ignore next -- この列は BASE_COLUMNS で width を指定済み */ (col.width || '148px'),
            getOptions: ({ row }) => middleCategoryOptionsByLarge[row?.largeCategory] || [''],
            onChange: ({ nextRow, value }) => {
              /* istanbul ignore next -- 大項目・中項目は選択式で未選択は空文字として入る */
              const middleId = middleByLargePair.get(`${nextRow.largeCategory || ''}::${value || ''}`);
              const smallOptions = (middleId != null && smallCategoryOptionsByMiddleId[middleId]) || [''];
              const keepSmall = smallOptions.includes(nextRow.smallItem || '');
              return {
                ...nextRow,
                smallItem: keepSmall ? (nextRow.smallItem || '') : '',
              };
            },
          };
        }

        if (col.key === 'smallItem') {
          return {
            ...col,
            width: /* istanbul ignore next -- この列は BASE_COLUMNS で width を指定済み */ (col.width || '148px'),
            // 小項目候補は大項目込みで解決した中項目IDでスコープ（課題自身のカテゴリとして選択）
            getOptions: ({ row }) => {
              const middleId = middleByLargePair.get(`${row?.largeCategory || ''}::${row?.middleCategory || ''}`);
              return (middleId != null && smallCategoryOptionsByMiddleId[middleId]) || [''];
            },
          };
        }

        if (col.key === 'owner') {
          return {
            ...col,
            width: /* istanbul ignore next -- この列は BASE_COLUMNS で width を指定済み */ (col.width || '148px'),
            getOptions: () => employeeOptions,
          };
        }

        if (col.key === 'riskManagement') {
          return {
            ...col,
            width: /* istanbul ignore next -- この列は BASE_COLUMNS で width を指定済み */ (col.width || '180px'),
            getOptions: ({ row }) => riskManagementOptionsByOemRegion[row?.oemRegion || ''] || [''],
          };
        }

        return {
          ...col,
          width: (col.width || '148px'),
          options:
            col.key === 'oemRegion'
              ? oemRegionOptions
              : col.key === 'status'
              ? statusOptions
              : col.key === 'ownerRegion'
              ? actorOptions
              : col.key === 'issueOrigin'
              ? causeOptions
              : col.key === 'impact'
              ? impactOptions
              : /* istanbul ignore next -- select 列はここまでの分岐で網羅している */ col.type === 'select'
              ? (selectOptions[col.key] || [''])
              : undefined,
        };
      }),
    [
      actorOptions,
      causeOptions,
      employeeOptions,
      impactOptions,
      largeCategoryOptions,
      middleByLargePair,
      middleCategoryOptionsByLarge,
      oemRegionOptions,
      riskManagementOptionsByOemRegion,
      selectOptions,
      smallCategoryOptionsByMiddleId,
      statusOptions,
    ],
  );

  const deadlineCheckOptions = useMemo(() => {
    const values = new Set();
    rows.forEach((row) => { if (row.deadlineCheck) values.add(row.deadlineCheck); });
    return Array.from(values);
  }, [rows]);

  const filteredRows = useMemo(() => {
    const predicate = buildFilterPredicate({
      deadlineCheck: (row, vals) => vals.includes(row.deadlineCheck),
      oemRegion: (row, vals) => vals.includes(row.oemRegion),
      status: (row, vals) => vals.includes(row.status),
      ownerRegion: (row, vals) => vals.includes(row.ownerRegion),
      largeCategory: (row, vals) => vals.includes(row.largeCategory),
      middleCategory: (row, vals) => vals.includes(row.middleCategory),
      smallItem: (row, vals) => vals.includes(row.smallItem),
      todo: (row, vals) => {
        /* istanbul ignore if -- 述語は値が選ばれている項目にしか呼ばれない */
        if (vals.length === 0) return true;
        if (vals.includes('true') && row.todo) return true;
        if (vals.includes('false') && !row.todo) return true;
        return false;
      },
      projectIssue: (row, vals) => {
        if (vals.includes('true') && row.projectIssue) return true;
        if (vals.includes('false') && !row.projectIssue) return true;
        return false;
      },
      owner: (row, vals) => vals.some((v) => (row.owner || '').includes(v)),
      date_from: (row, val) => {
        const d = row.dueDate;
        return d && d >= val;
      },
      date_to: (row, val) => {
        const d = row.dueDate;
        return d && d <= val;
      },
      keyword: (row, val) => {
        const keyword = val.trim().toLowerCase();
        if (!keyword) return true;
        const target = [
          row.summary, row.smallItem, row.closeCondition, row.policyDetail, row.riskManagement,
        ].filter(Boolean).join(' ').toLowerCase();
        return target.includes(keyword);
      },
    });
    return rows.filter(predicate);
  }, [rows, buildFilterPredicate]);

  const problemFilterFields = useMemo(
    () => [
      {
        key: 'deadlineCheck',
        label: '期限チェック',
        type: 'multiselect',
        options: deadlineCheckOptions.map((o) => ({ value: o, label: o })),
      },
      {
        key: 'oemRegion',
        label: 'OEM×対地',
        type: 'multiselect',
        options: oemRegionOptions.filter(Boolean).map((o) => ({ value: o, label: o })),
      },
      {
        key: 'status',
        label: 'ステータス',
        type: 'multiselect',
        options: (/* istanbul ignore next -- useState(['']) 初期化＋setter は常に配列を渡すため undefined にならない */ statusOptions || []).filter(Boolean).map((o) => ({ value: o, label: o })),
      },
      {
        key: 'ownerRegion',
        label: '領域',
        type: 'multiselect',
        options: (/* istanbul ignore next -- useState(['']) 初期化＋setter は常に配列を渡すため undefined にならない */ actorOptions || []).filter(Boolean).map((o) => ({ value: o, label: o })),
      },
      {
        key: 'largeCategory',
        label: '大項目',
        type: 'multiselect',
        options: (/* istanbul ignore next -- useState(['']) 初期化＋setter は常に配列を渡すため undefined にならない */ largeCategoryOptions || []).filter(Boolean).map((o) => ({ value: o, label: o })),
      },
      {
        key: 'middleCategory',
        label: '中項目',
        type: 'multiselect',
        getOptions: ({ values }) => {
          /* istanbul ignore next -- 高度フィルターは全キーを配列で初期化するため undefined にならない */
          const selectedLarge = values.largeCategory || [];
          let opts;
          if (selectedLarge.length > 0) {
            const merged = new Set();
            selectedLarge.forEach((lg) => {
              (middleCategoryOptionsByLarge[lg] || []).forEach((m) => { if (m) merged.add(m); });
            });
            opts = Array.from(merged);
          } else {
            opts = (/* istanbul ignore next -- buildSelectOptions の戻り値は引数 keys と同じキーを必ず持ち、参照側も同じ列集合を使う */ selectOptions.middleCategory || []).filter(Boolean);
          }
          return opts.map((o) => ({ value: o, label: o }));
        },
      },
      {
        key: 'smallItem',
        label: '小項目',
        type: 'multiselect',
        getOptions: ({ values }) => {
          /* istanbul ignore next -- 高度フィルターは全キーを配列で初期化するため undefined にならない */
          const selectedLarge = values.largeCategory || [];
          /* istanbul ignore next -- 高度フィルターは全キーを配列で初期化するため undefined にならない */
          const selectedMiddle = values.middleCategory || [];
          const merged = new Set();
          const addByMiddle = (mid) => {
            (smallCategoryOptionsByMiddle[mid] || []).forEach((s) => { if (s) merged.add(s); });
          };
          if (selectedMiddle.length > 0) {
            selectedMiddle.forEach(addByMiddle);
          } else if (selectedLarge.length > 0) {
            // 大項目のみ選択時は、その大項目配下の中項目すべての小項目を集約する
            selectedLarge.forEach((lg) => {
              (middleCategoryOptionsByLarge[lg] || []).forEach((mid) => { if (mid) addByMiddle(mid); });
            });
          } else {
            (/* istanbul ignore next -- buildSelectOptions の戻り値は引数 keys と同じキーを必ず持ち、参照側も同じ列集合を使う */ selectOptions.smallItem || []).filter(Boolean).forEach((s) => merged.add(s));
          }
          return Array.from(merged).map((o) => ({ value: o, label: o }));
        },
      },
      {
        key: 'todo',
        label: 'ToDo',
        type: 'multiselect',
        options: [
          { value: 'true', label: 'チェック' },
          { value: 'false', label: 'チェックなし' },
        ],
      },
      {
        key: 'projectIssue',
        label: 'PJ全体課題',
        type: 'multiselect',
        options: [
          { value: 'true', label: '該当あり' },
          { value: 'false', label: '該当なし' },
        ],
      },
      {
        key: 'owner',
        label: '担当者',
        type: 'multiselect',
        options: employeeOptions.filter(Boolean).map((o) => ({ value: o, label: o })),
      },
      { key: 'date_from', label: '期日From', type: 'date' },
      { key: 'date_to', label: '期日To', type: 'date' },
      {
        key: 'keyword',
        label: 'キーワード',
        type: 'text',
        className: 'pm-filter-item--keyword',
        placeholder: 'タイトル / 小項目 / クローズ条件 / 対応方針詳細 / 関連リスク',
      },
    ],
    [
      actorOptions,
      deadlineCheckOptions,
      employeeOptions,
      largeCategoryOptions,
      middleCategoryOptionsByLarge,
      oemRegionOptions,
      selectOptions.middleCategory,
      selectOptions.smallItem,
      smallCategoryOptionsByMiddle,
      statusOptions,
    ],
  );

  const handleExportConfirm = async (scope) => {
    const data = scope === 'filtered' ? filteredRows : rows;
    setExporting(true);
    try {
      await exportToExcel(data, BASE_COLUMNS, '課題管理');
    } finally {
      setExporting(false);
      setExportPopupOpen(false);
    }
  };

  const buildProblemRefs = () => ({
    statusByName,
    actorByName,
    causeByName,
    impactByName,
    largeByName,
    middleByLargePair,
    smallByMiddleIdPair,
    employeeByName,
    oemRegionLookup,
    riskByName,
  });

  const handlePopupDelete = async (row) => {
    /* istanbul ignore if -- 削除ボタンは既存レコード（id あり）にしか出ない */
    if (!row.id) throw new Error('新規レコードは削除できません');
    await deleteProblem(row.id, row.updated_at_iso ?? null);
    const data = await getProblems();
    setRows(data);
  };

  const handlePopupSave = async (row) => {
    const payload = toPayload(row, buildProblemRefs(), defaultCreateValues);
    if (!payload) throw new Error('必須項目が未入力です');
    let savedId;
    try {
      if (row.id) {
        await updateProblem(row.id, payload);
        savedId = row.id;
      } else {
        const created = await createProblem(payload);
        savedId = created?.id;
      }
    } catch (e) {
      if (e.status === 409) {
        throw new Error('他のユーザーによってこのレコードが更新されました。\n入力した内容は画面に残っています。\nOK を押した後、メモなど必要な内容をコピーしてから\nキャンセルで閉じて最新データを確認してください。');
      }
      throw e;
    }
    const data = await getProblems();
    setRows(data);
    if (savedId != null) {
      setHighlightedRowId(savedId);
      /* istanbul ignore next -- 5秒後にハイライトを解除するタイマー（jsdom で未発火） */
      setTimeout(() => setHighlightedRowId(null), 5000);
    }
  };


  return (
    <>
    <DashboardInputLayout
      inputTabIndex={3}
      toolbar={
        !loading && !error ? (
          <DashboardInputToolbar
            countText={`${filteredRows.length} / ${rows.length} 件を表示`}
            exporting={exporting}
            onAdd={() => setAddSignal((prev) => prev + 1)}
            onExport={() => setExportPopupOpen(true)}
            onToggleFilters={() => setShowFilters((prev) => !prev)}
            showAdd
            showExport
            showFilterToggle
            showFilters={showFilters}
          />
        ) : null
      }
      filters={
        !loading && !error && showFilters ? (
          <AdvancedFilterPanel
            fields={problemFilterFields}
            filterMode={filterMode}
            onClear={clearFilters}
            onFieldChange={handleProblemFilterChange}
            onFilterModeChange={setFilterMode}
            values={problemFilterValues}
          />
        ) : null
      }
    >
      {loading && <div>読み込み中...</div>}
      {!loading && error && <div>{error}</div>}
      {!loading && !error && (
      <EditableRecordTable
        addButtonLabel="+ レコード追加"
        columns={columns}
        createEmptyRow={() => createEmptyRow(defaultCreateValues)}
        enableRowPopup
        externalAddSignal={addSignal}
        highlightedRowId={highlightedRowId}
        initialRows={filteredRows}
        onPopupDelete={handlePopupDelete}
        onPopupSave={handlePopupSave}
        onRowsChange={setEditingRows}
        readOnly={!canEdit('ダッシュボード')}
        rowPopupTitle="課題管理レコード詳細"
        showAddButton={false}
      />
      )}
    </DashboardInputLayout>
    <ExportConfirmPopup
      open={exportPopupOpen}
      onClose={() => setExportPopupOpen(false)}
      onExport={handleExportConfirm}
      filteredCount={filteredRows.length}
      totalCount={rows.length}
      loading={exporting}
      sheetName="課題管理"
      filterFields={problemFilterFields}
      filterValues={problemFilterValues}
    />
    </>
  );
}
